/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Pipeline Metrics Hook
 *
 * Returns metric calculators for the pipeline view based on opportunities (estimates).
 * Tracks opportunities, pipeline value, and conversion metrics.
 */

"use client";

import type { PipelineOpportunity } from "@/hooks/data/estimates";
import { formatPercentage } from "@/lib/utils/formatters";
import type { DashboardMetricCalculator } from "@/types/dashboard-metrics";
import { DollarSign, Target, TrendingUp, Users } from "lucide-react";
import { useMemo } from "react";

interface UsePipelineMetricsOptions {
  isLoading: boolean;
  hasActiveFilters: boolean;
  formatCurrency: (amount: number | null) => string;
}

/**
 * Hook that returns metric calculators for the pipeline view
 */
export function usePipelineMetrics({
  isLoading,
  hasActiveFilters,
  formatCurrency,
}: UsePipelineMetricsOptions): DashboardMetricCalculator<PipelineOpportunity>[] {
  return useMemo(
    () => [
      {
        id: "pipeline-opportunities",
        calculate: (data: PipelineOpportunity[]) => ({
          id: "pipeline-opportunities",
          title: "Opportunities",
          value: isLoading ? "--" : data.length.toLocaleString(),
          subtitle: hasActiveFilters ? "Filtered selection" : "Active pipeline",
          icon: Target,
          variant: "info" as const,
        }),
      },
      {
        id: "pipeline-value",
        calculate: (data: PipelineOpportunity[]) => {
          const totalValue = data.reduce(
            (sum, opportunity) => sum + (opportunity.value || 0),
            0,
          );
          const wonCount = data.filter((o) => o.stage === "APPROVED").length;
          const conversionRate = data.length
            ? (wonCount / data.length) * 100
            : 0;
          return {
            id: "pipeline-value",
            title: "Pipeline Value",
            value: isLoading ? "--" : formatCurrency(totalValue),
            subtitle: `Conversion ${formatPercentage(conversionRate, 2)} · ${wonCount} won`,
            icon: DollarSign,
            variant: "success" as const,
          };
        },
      },
      {
        id: "active-stages",
        calculate: (data: PipelineOpportunity[]) => {
          const inProcess = data.filter((o) => o.stage === "IN_PROCESS").length;
          const submitted = data.filter((o) => o.stage === "SUBMITTED").length;
          return {
            id: "active-stages",
            title: "In Motion",
            value: isLoading ? "--" : `${inProcess + submitted}`,
            subtitle: `${inProcess} in process · ${submitted} submitted`,
            icon: TrendingUp,
            variant: "default" as const,
          };
        },
      },
      {
        id: "unique-clients",
        calculate: (data: PipelineOpportunity[]) => {
          const uniqueClients = new Set(
            data
              .map((o) => o.clientId)
              .filter((id): id is string => id !== null),
          ).size;
          return {
            id: "unique-clients",
            title: "Clients",
            value: isLoading ? "--" : uniqueClients.toLocaleString(),
            subtitle: "Unique clients in pipeline",
            icon: Users,
            variant: "default" as const,
          };
        },
      },
    ],
    [formatCurrency, hasActiveFilters, isLoading],
  );
}
